import axios from "axios";
import { authenticatorApi } from "@/infrastructure/api/authenticatorApi";
import type {
  BusinessImageType,
  CategoryOption,
  ProfileFormData,
} from "@/pages/perfil/types";

type ApiBusinessImage = {
  id: string;
  tipo: BusinessImageType;
  url: string;
  alt_text: string | null;
};

export type ProfileResponse = {
  nome: string;
  email: string;
  cpf: string | null;
  contato: string | null;
  pcd: boolean;
  deficiencia: string | null;
  maeAtipica: boolean;
  titulo: string | null;
  cnpj: string | null;
  website?: string | null;
  categoria: string | null;
  associada: boolean;
  instituto: string | null;
  instagram: string | null;
  facebook: string | null;
  tiktok: string | null;
  descricao: string | null;
  cep: string | null;
  rua: string | null;
  numero: string | null;
  complemento: string | null;
  bairro: string | null;
  municipio: string | null;
  preferencia: string | null;
  imagensNegocio?: {
    logo: ApiBusinessImage | null;
    banner: ApiBusinessImage | null;
  } | null;
};

type CategoryResponse = { id: number; nome: string };

function messageFrom(error: unknown, fallback: string) {
  if (!axios.isAxiosError(error))
    return error instanceof Error ? error.message : fallback;
  if (error.response?.status === 401 || error.response?.status === 403)
    return "Sua sessão expirou. Entre novamente.";
  if (!error.response) return "Não foi possível conectar ao servidor.";
  const detail = error.response.data?.detail;
  return typeof detail === "string" && detail ? detail : fallback;
}

async function safeRequest<T>(request: () => Promise<T>, fallback: string) {
  try {
    return await request();
  } catch (error) {
    throw new Error(messageFrom(error, fallback));
  }
}

export function getProfile() {
  return safeRequest(
    async () =>
      (await authenticatorApi.get<ProfileResponse>("/empreendedoras/perfil"))
        .data,
    "Não foi possível carregar os dados do perfil.",
  );
}

export function getCategories() {
  return safeRequest(async () => {
    const { data } =
      await authenticatorApi.get<CategoryResponse[]>("/categorias");
    return data.map(
      (item) =>
        ({ label: item.nome, value: String(item.id) }) satisfies CategoryOption,
    );
  }, "Não foi possível carregar as categorias.");
}

const identityPayload = (data: ProfileFormData) => ({
  nome: data.fullName,
  email: data.email,
  cpf: data.cpf,
  telefone: data.phone,
  pcd: data.isDisabledPerson,
  deficiencia: data.isDisabledPerson ? data.disabilityType : "",
  maeAtipica: data.isAtypicalMother,
});

const businessPayload = (data: ProfileFormData) => ({
  ...identityPayload(data),
  nomeNegocio: data.businessName,
  cnpj: data.cnpj,
  website: data.website,
  categoriaId: Number(data.businessCategory),
  participaAssociacao: data.participatesInAssociation,
  instituto: data.participatesInAssociation ? data.associationName : "",
  instagram: data.instagram,
  facebook: data.facebook,
  tiktok: data.tiktok,
  descricao: data.businessDescription,
});

export function saveProfileStepOne(data: ProfileFormData) {
  return safeRequest(
    async () =>
      (
        await authenticatorApi.put(
          "/empreendedoras/perfil/step1",
          identityPayload(data),
        )
      ).data,
    "Não foi possível salvar o rascunho.",
  );
}

export function saveProfileStepTwo(data: ProfileFormData) {
  return safeRequest(
    async () =>
      (
        await authenticatorApi.put(
          "/empreendedoras/perfil/step2",
          businessPayload(data),
        )
      ).data,
    "Não foi possível salvar o rascunho.",
  );
}

export function submitProfile(data: ProfileFormData) {
  const preference = {
    neighborhood_city: "MUNICIPIO",
    full_address: "COMPLETO",
    online_or_on_request: "ONLINE",
  }[data.locationDisplayPreference];
  return safeRequest(
    async () =>
      (
        await authenticatorApi.put("/empreendedoras/perfil", {
          nome: data.fullName,
          email: data.email,
          cpf: data.cpf,
          contato: data.phone,
          pcd: data.isDisabledPerson,
          deficiencia: data.isDisabledPerson ? data.disabilityType : "",
          maeAtipica: data.isAtypicalMother,
          titulo: data.businessName,
          cnpj: data.cnpj,
          website: data.website,
          categoriaId: Number(data.businessCategory),
          associada: data.participatesInAssociation,
          instituto: data.participatesInAssociation ? data.associationName : "",
          instagram: data.instagram,
          facebook: data.facebook,
          tiktok: data.tiktok,
          descricao: data.businessDescription,
          cep: data.zipCode,
          rua: data.address,
          numero: data.addressNumber,
          complemento: data.addressComplement,
          bairro: data.neighborhood,
          municipio: data.city,
          preferencia: preference,
        })
      ).data,
    "Não foi possível cadastrar o perfil.",
  );
}

export async function uploadBusinessImage(
  file: File,
  altText: string,
  type: BusinessImageType,
) {
  // Proxy temporário: mantém o browser restrito à origem permitida pelo CSP.
  const formData = new FormData();
  formData.append("file", file);
  formData.append("tipo", type);
  formData.append("alt_text", altText);

  await safeRequest(
    () =>
      authenticatorApi.post("/negocios/imagens/proxy-upload", formData, {
        // Sobrescreve o application/json padrão; o Axios inclui o boundary.
        headers: { "Content-Type": "multipart/form-data" },
      }),
    "Não foi possível enviar a imagem do negócio.",
  );

  /*
   * ROLLBACK: fluxo anterior com upload-url, PUT direto no storage e confirmar.
   * const upload = await authenticatorApi.post("/negocios/imagens/upload-url", {
   *   tipo: type, alt_text: altText, content_type: file.type, tamanho_bytes: file.size,
   * });
   * await fetch(upload.data.upload_url, {
   *   method: "PUT", headers: { "Content-Type": file.type }, body: file,
   * });
   * await authenticatorApi.post(`/negocios/imagens/${upload.data.imagem_id}/confirmar`);
   */
}

export function deleteBusinessImage(id: string) {
  return safeRequest(
    () => authenticatorApi.delete(`/negocios/imagens/${id}`),
    "Não foi possível excluir a imagem.",
  );
}

export async function lookupZipCode(zipCode: string) {
  const digits = zipCode.replace(/\D/g, "");
  const { data } = await axios.get<{
    erro?: boolean;
    logradouro: string;
    bairro: string;
    localidade: string;
  }>(`https://viacep.com.br/ws/${digits}/json/`);
  if (data.erro) throw new Error("CEP não encontrado.");
  return data;
}
